// courses/{id}/lessons/{id}/videos/{id} documents plus the YouTube helpers.
// Moved from aura-courses-admin; every change recomputes the lesson and
// course durations as before.

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  writeBatch,
  type DocumentSnapshot,
} from "firebase/firestore";
import { getCoursesFirestore } from "../../config/firebase";
import type { Video, VideoFormData } from "../../types/course";
import { COURSES_COLLECTION, LESSONS_COLLECTION, VIDEOS_COLLECTION } from "./course.service";
import { updateLessonDuration } from "./lesson.service";

function videosRef(courseId: string, lessonId: string) {
  return collection(getCoursesFirestore(), COURSES_COLLECTION, courseId, LESSONS_COLLECTION, lessonId, VIDEOS_COLLECTION);
}

function videoRef(courseId: string, lessonId: string, videoId: string) {
  return doc(getCoursesFirestore(), COURSES_COLLECTION, courseId, LESSONS_COLLECTION, lessonId, VIDEOS_COLLECTION, videoId);
}

function toVideo(snap: DocumentSnapshot): Video {
  const data = snap.data() ?? {};
  return { id: snap.id, ...data, createdAt: data.createdAt?.toDate() } as Video;
}

export async function getVideosByLessonId(courseId: string, lessonId: string): Promise<Video[]> {
  const snapshot = await getDocs(query(videosRef(courseId, lessonId), orderBy("order", "asc")));
  return snapshot.docs.map(toVideo);
}

export async function getVideoById(courseId: string, lessonId: string, videoId: string): Promise<Video | null> {
  const snap = await getDoc(videoRef(courseId, lessonId, videoId));
  return snap.exists() ? toVideo(snap) : null;
}

export async function createVideo(courseId: string, lessonId: string, videoData: VideoFormData): Promise<string> {
  const docRef = await addDoc(videosRef(courseId, lessonId), { ...videoData, createdAt: Timestamp.now() });
  await updateLessonDuration(courseId, lessonId);
  return docRef.id;
}

export async function updateVideo(
  courseId: string,
  lessonId: string,
  videoId: string,
  videoData: Partial<Video>,
): Promise<void> {
  const updateData: Partial<Video> = { ...videoData };
  delete updateData.createdAt;
  delete updateData.id;
  await updateDoc(videoRef(courseId, lessonId, videoId), updateData);
  if (videoData.duration !== undefined) await updateLessonDuration(courseId, lessonId);
}

export async function deleteVideo(courseId: string, lessonId: string, videoId: string): Promise<void> {
  await deleteDoc(videoRef(courseId, lessonId, videoId));
  await updateLessonDuration(courseId, lessonId);
}

export async function reorderVideos(courseId: string, lessonId: string, videoIds: string[]): Promise<void> {
  const batch = writeBatch(getCoursesFirestore());
  videoIds.forEach((videoId, index) => {
    batch.update(videoRef(courseId, lessonId, videoId), { order: index });
  });
  await batch.commit();
}

export function extractYouTubeId(url: string): string | null {
  const patterns = [/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/, /youtube\.com\/embed\/([^&\n?#]+)/];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
}

// Thumbnail derived from the URL; empty string when it is not a YouTube URL.
export function getYouTubeThumbnail(url: string): string {
  const videoId = extractYouTubeId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : "";
}
